import React, { memo, useEffect, useRef, useState } from "react";
import { Box, Typography, Divider } from "@mui/material";
import { useParams } from "react-router-dom";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import CustomTable from "../../../../../../shared/components/table/custom-table";
import { MRT_ColumnDef } from "material-react-table";
import { fetchIncomingTcDetail } from "./incoming-details-apis";
import { IncomingTcDetailPanelProps, IncomingTcEntry } from "./incoming-details.model";
import { formatTcV4Decimal } from "../../../tc-v4-decimals";

const RenderDetailPanel = memo(({ row }: IncomingTcDetailPanelProps) => {
  const { TE_ID } = useParams();
  const { executeGet } = useApi();
  const [incomingDetail, setIncomingDetail] = useState<IncomingTcEntry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [localLoading, setLocalLoading] = useState(false);
  const hasFetchedRef = useRef(false);

  const isExpanded = typeof row.getIsExpanded === "function" ? row.getIsExpanded() : true;

  const incomingTcId = row.original.incomingTcId 
  const productId = row.original.productId || row.original.product_id || undefined;

  useEffect(() => {
    if (!isExpanded) return; 
    if (hasFetchedRef.current) return;
    if (!TE_ID) return;
    if (!incomingTcId && !productId) {
      setError("Insufficient identifiers to fetch incoming detail.");
      return;
    }

    hasFetchedRef.current = true;
    setLocalLoading(true);
    setError(null);

    fetchIncomingTcDetail(executeGet, TE_ID, incomingTcId, productId)
      .then((data) => {
        setIncomingDetail(data);
      })
      .catch((err) => {
        console.error("Fetch error:", err);
        setError(err.message || "Failed to load details");
      })
      .finally(() => {
        setLocalLoading(false);
      });
  }, [isExpanded, incomingTcId, productId, TE_ID, executeGet]);

  if (localLoading) return <LoaderComponent />;
  if (error) return (
    <Box sx={{ width: '100%', p: 5 }}>
      <Typography color="error">{error}</Typography>
    </Box>
  );

  if (!incomingDetail) {
    if (!isExpanded) return (
      <Box sx={{ width: '100%', p: 2 }}>
        <Typography>Expand to load details</Typography>
      </Box>
    );

    return (
      <Box sx={{ width: '100%', p: 5 }}>
        <Typography>No incoming detail information available.</Typography>
      </Box>
    );
  }

  const incomingTcInfo = incomingDetail.incomingTcInfo;
  const productNetWeight = incomingDetail.productNetWeight;
  const inputTcStandard = incomingDetail.inputTcStandard || [];

  // Determine if flag seller is Yes
  const anyDetail: any = incomingDetail;
  const isFlagSellerYes = String(anyDetail.incomingTcInfo?.isFlagSeller ?? anyDetail.isFlagSeller ?? 'No') === 'Yes';

  // Data Model Relationship:
  // - inputTcStandard: Array of objects with product-specific standards
  //   [{ incomingProductArticleNo: string, incomingProductStandard: string[], ... }]
  // - tcIncomingTCStandard: Flat array of all standards (aggregated from inputTcStandard)
  //   string[] (e.g., ["GRS", "RCS"])
  // The tcIncomingTCStandard is derived by aggregating all incomingProductStandard arrays from inputTcStandard

  // Get standards value based on flag seller status
  // Uses tcIncomingTCStandard (aggregated flat array) for single value display
  const getStandardsValue = () => {
    if (isFlagSellerYes) {
      return "na";
    }
    // Use aggregated tcIncomingTCStandard from incomingTcInfo
    if (Array.isArray(incomingTcInfo.tcIncomingTCStandard) && incomingTcInfo.tcIncomingTCStandard.length > 0) {
      return incomingTcInfo.tcIncomingTCStandard.join(", ");
    }
    // Fallback: aggregate from inputTcStandard if tcIncomingTCStandard is not available
    if (inputTcStandard.length > 0) {
      const aggregated = inputTcStandard.reduce<string[]>((acc, entry) => {
        if (Array.isArray(entry.incomingProductStandard)) {
          acc.push(...entry.incomingProductStandard);
        }
        return acc;
      }, []);
      const uniqueStandards = Array.from(new Set(aggregated));
      return uniqueStandards.length > 0 ? uniqueStandards.join(", ") : "N/A";
    }
    return "N/A";
  };

  const incomingTcInfoColumns: MRT_ColumnDef<any>[] = [
    { accessorKey: "associatedProductArticleNo", header: "Associated Product Article Number", minSize: 200, maxSize: 300 },
    { accessorKey: "incomingTCNo", header: "Incoming TC Number", minSize: 150, maxSize: 250 },
    { accessorKey: "incomingATUNo", header: "Incoming ATU Number", minSize: 150, maxSize: 250 },
    { accessorKey: "incomingRMDFNo", header: "Incoming RMDF Number", minSize: 150, maxSize: 250 },
    { accessorKey: "incomingFPFNo", header: "Incoming FPF Number", minSize: 150, maxSize: 250 },
    { accessorKey: "incomingProductArticleNo", header: "Incoming Product Article Number", minSize: 200, maxSize: 400 },
  ];

  const incomingTcInfoData = [
    {
      associatedProductArticleNo: incomingTcInfo.tcAssociatedProductArticleNo || "N/A",
      incomingTCNo: incomingTcInfo.tcIncomingTCNo || "N/A",
      incomingATUNo: incomingTcInfo.tcIncomingATCNo || "N/A",
      incomingRMDFNo: incomingTcInfo.tcIncomingRMDFNo || "N/A",
      incomingFPFNo: incomingTcInfo.tcIncomingFPFNo || "N/A",
      incomingProductArticleNo: Array.isArray(incomingTcInfo.tcIncomingProductNo) 
        ? incomingTcInfo.tcIncomingProductNo.join(", ") 
        : incomingTcInfo.tcIncomingProductNo || "N/A",
    },
  ];

  const formatStandards = (standards: any): string => {
    const values = Array.isArray(standards) ? standards : standards ? [standards] : [];
    const filtered = values
      .map((standard) => String(standard || "").trim())
      .filter((standard) => standard && standard.toLowerCase() !== "na");
    return filtered.length > 0 ? Array.from(new Set(filtered)).join(", ") : "N/A";
  };

  const standardsByArticle: Record<string, string[]> = {};
  inputTcStandard.forEach((item) => {
    const article = item.incomingProductArticleNo;
    if (article) {
      standardsByArticle[article] = item.incomingProductStandard || [];
    }
  });
  Object.entries((productNetWeight as any).standards || {}).forEach(([article, standards]) => {
    if (!standardsByArticle[article]) {
      standardsByArticle[article] = Array.isArray(standards) ? standards : [];
    }
  });
  const getArticleStandard = (article: string) =>
    formatStandards(standardsByArticle[article] || incomingTcInfo.tcIncomingTCStandard);

  const productNetWeightColumns: MRT_ColumnDef<any>[] = [
    { accessorKey: "productNo", header: "Product Article Number", minSize: 150, maxSize: 250 },
    { accessorKey: "standard", header: "STD", minSize: 120, maxSize: 180 },
    { accessorKey: "netWeight", header: "Net Weight Used", minSize: 150, maxSize: 200 },
  ];


  let productNetWeightData = Object.entries(productNetWeight.netWeights || {}).map(([productNo, netWeight]) => ({
    productNo,
    standard: getArticleStandard(productNo),
    netWeight: formatTcV4Decimal(netWeight),
  }));



  const rawMaterialsTableData: any[] = Object.entries(productNetWeight.rawMaterials || {}).flatMap(([productNo, rms]) => {
    if (!Array.isArray(rms)) return [];
    return rms.map((rm: any) => ({
      productNo,
      standard: getArticleStandard(productNo),
      tcProductCategoryCode: rm.tcProductCategoryCode || rm.tc_product_category_code || "",
      tcProductDetailCode: rm.tcProductDetailCode || rm.tc_product_detail_code || "",
      isCertified: rm.isCertified || rm.is_certified || "",
      rawMaterialCode: rm.rawMaterialCode || rm.raw_material_code || rm.tcProductIncomingCertifiedRawMaterialCode || "",
      rawMaterialWeight: Number(rm.rawMaterialWeight || rm.raw_material_weight || rm.tcProductIncomingCertifiedRawMaterialWeightUsed || 0),
      rawMaterialPercentage: Number(rm.rawMaterialPercentage || rm.raw_material_percentage || 0),
    }));
  });

  const finalRawMaterialsTableData = rawMaterialsTableData;

  const rawMaterialsColumns: MRT_ColumnDef<any>[] = [
    { accessorKey: "productNo", header: "Product No.", minSize: 120 },
    { accessorKey: "standard", header: "STD", minSize: 100 },
    { accessorKey: "tcProductCategoryCode", header: "PC", minSize: 90 },
    { accessorKey: "tcProductDetailCode", header: "PD", minSize: 90 },
    { accessorKey: "isCertified", header: "Certified", minSize: 100 },
    { accessorKey: "rawMaterialCode", header: "RM", minSize: 100 },
    { accessorKey: "rawMaterialWeight", header: "RM Wt Used", minSize: 120 },
    { accessorKey: "rawMaterialPercentage", header: "RM%", minSize: 90 },
  ];



  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 4,
        gap: 4.5,
        width: '100%',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        border: '1.5px solid #ffb300',
        boxShadow: '0px 4px 20px #ffb30018',
        boxSizing: 'border-box',
        '& .MuiPaper-root': {
          boxShadow: 'none !important',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          overflow: 'hidden',
          backgroundColor: '#ffffff !important',
        },

        '& .MuiTableCell-head': {
          backgroundColor: '#E1ECEB !important',
          color: '#2C4748 !important',
          height: 'auto !important',
          py: '8px !important',
          borderBottom: '1px solid #cbd5e1 !important',
        },
        '& .MuiTableCell-head *': {
          color: '#2C4748 !important',
          fontWeight: '600 !important',
          fontSize: '0.75rem !important',
          textTransform: 'uppercase !important',
          letterSpacing: '0.03em !important',
        },
        '& .MuiTableCell-body': {
          py: '8px !important',
          fontSize: '0.8rem !important',
          color: '#475569 !important',
          borderBottom: '1px solid #f1f5f9 !important',
        },
        '& .MuiTableRow-root:last-child .MuiTableCell-body': {
          borderBottom: 'none !important',
        },
        '& .MuiDivider-root': {
          width: '100%',
          borderColor: '#e2e8f0',
          my: 1,
        }
      }}
    >
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#2C4748',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              display: 'inline-block',
              width: '4px',
              height: '14px',
              backgroundColor: '#2C4748',
              borderRadius: '2px',
              mr: 1,
            }
          }}
        >
          Incoming TC Information
        </Typography>
        {incomingTcInfo ? (
          <CustomTable
            columns={incomingTcInfoColumns}
            data={incomingTcInfoData}
            enableTopToolbar={false}
            enableBottomToolbar={false}
            enableSorting={false}
            enableColumnFilters={false}
          />
        ) : (
          <Typography variant="body2" color="textSecondary">
            No incoming TC information available.
          </Typography>
        )}
      </Box>

      <Divider />

      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#2C4748',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              display: 'inline-block',
              width: '4px',
              height: '14px',
              backgroundColor: '#2C4748',
              borderRadius: '2px',
              mr: 1,
            }
          }}
        >
          Product Net Weight Information
        </Typography>
        {productNetWeightData.length > 0 ? (
          <CustomTable
            columns={productNetWeightColumns}
            data={productNetWeightData}
            enableTopToolbar={false}
            enableBottomToolbar={false}
            enableSorting={false}
            enableColumnFilters={false}
          />
        ) : (
          <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', pl: 1.5, fontSize: '0.8rem' }}>
            No records to display
          </Typography>
        )}
      </Box>

      <Divider />

      <Box sx={{ width: '100%', textAlign: 'left' }} display="grid">
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#2C4748',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              display: 'inline-block',
              width: '4px',
              height: '14px',
              backgroundColor: '#2C4748',
              borderRadius: '2px',
              mr: 1,
            }
          }}
        >
          Incoming TC Raw Materials Information
        </Typography>
        {finalRawMaterialsTableData.length > 0 ? (
          <CustomTable
            columns={rawMaterialsColumns}
            data={finalRawMaterialsTableData}
            enableTopToolbar={false}
            enableBottomToolbar={false}
            enableSorting={false}
            enableColumnFilters={false}
          />
        ) : (
          <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', pl: 1.5, fontSize: '0.8rem' }}>
            No records to display
          </Typography>
        )}
      </Box>
    </Box>
  );
});

RenderDetailPanel.displayName = "RenderDetailPanel";

export default RenderDetailPanel;
